import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, LabelList,
} from 'recharts';
import { getGPS, getPlayers, getAvailability } from '../../api';
import { useSeason } from '../../SeasonContext';
import { downloadBlob, renderReportPdf } from '../../pdfExport';
import { useUrlParam } from '../../hooks/useUrlParam';
import { getISOWeek, getISOYear, isoKey, computePlayerSessions, groupIntoWeeks } from './playerChronicShared';
import { EstimatedValue, EstimatedMarker, NotEstimatedFlag } from '../../components/EstimatedMarker';

// ── Helpers ────────────────────────────────────────────────────────────────────
// getISOWeek/getISOYear/isoKey and the playerSessions/allWeeks computation moved to
// playerChronicShared.js (2026-09-10) so PlayerChronicPanel.js (the Einstein compose panel)
// reuses the exact same weekly-grouping logic instead of a second hand-copy. Behavior here is
// unchanged — still used below by renderBuckets over activeDates, a separate computation this
// extraction didn't touch.
function fmtDate(dateStr) {
  if (!dateStr) return '';
  const p = dateStr.split('-');
  return `${p[2]}/${p[1]}`;
}

// Pure UTC arithmetic on 'YYYY-MM-DD' strings (same as scenarioDays.js's addDaysUTC). The
// earlier local-Date version round-tripped through toISOString(), which in CET/CEST stalled on
// the spring DST day (dateRangeInclusive then never terminated) and put isoWeekBounds a day
// early (Sun–Sat) all year.
function addDaysStr(dateStr, n) {
  const [y, m, d] = dateStr.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d + n)).toISOString().slice(0, 10);
}
function dateRangeInclusive(from, to) {
  const dates = [];
  let cur = from;
  while (cur <= to) { dates.push(cur); cur = addDaysStr(cur, 1); }
  return dates;
}
// Monday..Sunday calendar span for a given ISO year/week — lets the weekly tables enumerate
// every day of a selected week, not just the days that happen to have a recorded session.
function isoWeekBounds(year, week) {
  const jan4Dow = new Date(Date.UTC(year, 0, 4)).getUTCDay() || 7;
  const monday = addDaysStr(`${year}-01-04`, 1 - jan4Dow + (week - 1) * 7);
  return [monday, addDaysStr(monday, 6)];
}

// Short badge text for an unavailable day, derived from computeAvailabilityDay's own `detail`
// string (availability.js) rather than re-deriving injury/illness/medical-stage logic a second
// time here — that string already ends with the stage or status in parentheses for injuries and
// illnesses ("Hamstring - Strain (OFR)", "Flu (reported)"); an override falls back to its own
// free-text reason since it has no such suffix.
function shortAvailLabel(day) {
  if (!day || day.status !== 'unavailable') return null;
  const cap = s => s.charAt(0).toUpperCase() + s.slice(1);
  const m = /\(([^)]+)\)\s*$/.exec(day.detail || '');
  if (m) return cap(m[1]);
  if (day.reason && day.reason !== 'none') return cap(day.reason);
  return 'Unavailable';
}

const fmtNum   = n => (n == null || isNaN(n)) ? '—' : Math.round(n).toLocaleString();
const sumField = (arr, key) => arr.reduce((s, x) => s + (Number(x[key]) || 0), 0);

// ── Player avatar (photo or initials fallback) ────────────────────────────────
function PlayerAvatar({ player }) {
  const [imgErr, setImgErr] = useState(false);
  if (!player) return null;
  const initials = (player.name || '?').charAt(0).toUpperCase();
  const showImg  = player.photo_url && !imgErr;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
      {showImg ? (
        <>
          {/* Hidden img purely for error detection */}
          <img src={player.photo_url} alt="" onError={() => setImgErr(true)} style={{ display: 'none' }} />
          {/* background-image renders correctly in html2canvas (objectFit:cover does not) */}
          <div style={{
            width: 56, height: 56, borderRadius: 10, flexShrink: 0,
            backgroundImage: `url(${player.photo_url})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center top',
            border: '1px solid var(--border)',
          }} />
        </>
      ) : (
        <div style={{ width: 56, height: 56, borderRadius: 10, background: 'linear-gradient(135deg,#2563eb,#3b82f6)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: '1.3rem', color: 'white', flexShrink: 0 }}>
          {initials}
        </div>
      )}
      <div>
        <div style={{ fontWeight: 700, fontSize: '1rem', color: 'var(--text-primary)' }}>{player.name}</div>
        <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginTop: 2 }}>Player Chronic Report</div>
      </div>
    </div>
  );
}

// ── Weekly table ───────────────────────────────────────────────────────────────
const TH_STYLE = {
  padding: '8px 10px',
  fontSize: '0.67rem',
  fontWeight: 600,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  color: 'var(--text-muted)',
  whiteSpace: 'nowrap',
  borderBottom: '1px solid var(--border)',
  background: 'var(--bg-app)',
};

function td(align = 'right', extra = {}) {
  return {
    padding: '7px 10px',
    fontSize: '0.78rem',
    color: 'var(--text-primary)',
    textAlign: align,
    borderBottom: '1px solid var(--border)',
    ...extra,
  };
}

// days: [{ date, session: {...}|null, avail: availabilityDay|null }] — a day appears here only
// when it has a session or a flagged-unavailable status (see the renderBuckets useMemo below);
// an ordinary rest day with neither stays invisible, same as before this feature existed.
function WeekTable({ weekNum, days }) {
  const sessionRows = days.filter(d => d.session).map(d => d.session);
  const totals = {
    td:   sumField(sessionRows, 'td'),
    rd:   sumField(sessionRows, 'rd'),
    hid:  sumField(sessionRows, 'hid'),
    sd:   sumField(sessionRows, 'sd'),
    sa:   sumField(sessionRows, 'sa'),
    hmld: sumField(sessionRows, 'hmld'),
  };
  const notEst = key => sessionRows.filter(r => r.isEstimated && r[key] == null).length;

  return (
    <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 12, overflow: 'hidden', marginBottom: 16 }}>
      <div style={{ padding: '11px 14px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 8, background: 'var(--bg-card)' }}>
        <span style={{ fontSize: '0.84rem', fontWeight: 700, color: 'var(--text-primary)' }}>Week {weekNum}</span>
        <span style={{ fontSize: '0.73rem', color: 'var(--text-muted)' }}>
          {sessionRows.length} session{sessionRows.length !== 1 ? 's' : ''}
        </span>
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 560 }}>
          <thead>
            <tr>
              <th style={{ ...TH_STYLE, textAlign: 'left' }}>Date</th>
              <th style={{ ...TH_STYLE, textAlign: 'left' }}>Session</th>
              <th style={{ ...TH_STYLE, textAlign: 'right' }}>Total Dist.</th>
              <th style={{ ...TH_STYLE, textAlign: 'right' }}>Run &gt;14.4</th>
              <th style={{ ...TH_STYLE, textAlign: 'right' }}>HID &gt;19.8</th>
              <th style={{ ...TH_STYLE, textAlign: 'right' }}>Sprint &gt;25.2</th>
              <th style={{ ...TH_STYLE, textAlign: 'right' }}>Short Actions</th>
              <th style={{ ...TH_STYLE, textAlign: 'right' }}>HMLD</th>
            </tr>
          </thead>
          <tbody>
            {days.map((d, i) => {
              const s = d.session;
              const badge = shortAvailLabel(d.avail);
              // Very subtle — a tint, not a highlight; the badge itself is what carries the
              // information, this just makes the row easy to spot while scanning.
              const rowBg = badge ? 'rgba(234,179,8,0.08)' : (i % 2 !== 0 ? 'var(--bg-row-alt)' : 'transparent');
              return (
                <tr key={d.date} style={{ background: rowBg }}>
                  <td style={td('left')}>
                    {fmtDate(d.date)}
                    {badge && (
                      <span style={{ marginLeft: 8, fontSize: '0.62rem', fontWeight: 700, letterSpacing: '0.03em', padding: '2px 6px', borderRadius: 999, background: 'rgba(234,179,8,0.22)', color: '#eab308', whiteSpace: 'nowrap' }} title={d.avail?.detail || badge}>
                        {badge}
                      </span>
                    )}
                  </td>
                  <td style={{ ...td('left'), maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: 'var(--text-secondary)' }}>
                    {s ? (s.title || '—') : '—'}
                    {s?.isEstimated && <EstimatedMarker basis={s.estimate} />}
                  </td>
                  <td style={td('right')}>{s ? <EstimatedValue value={s.td} basis={s.estimate} metric="td" format={fmtNum} /> : '—'}</td>
                  <td style={td('right')}>{s ? <EstimatedValue value={s.rd} basis={s.estimate} metric="rd" format={fmtNum} /> : '—'}</td>
                  <td style={td('right')}>{s ? <EstimatedValue value={s.hid} basis={s.estimate} metric="hid" format={fmtNum} /> : '—'}</td>
                  <td style={td('right')}>{s ? <EstimatedValue value={s.sd} basis={s.estimate} metric="sd" format={fmtNum} /> : '—'}</td>
                  <td style={td('right')}>{s ? <EstimatedValue value={s.sa} basis={s.estimate} metric="sa" format={fmtNum} /> : '—'}</td>
                  <td style={td('right')}>{s ? <EstimatedValue value={s.hmld} basis={s.estimate} metric="hmld" format={fmtNum} /> : '—'}</td>
                </tr>
              );
            })}
            {/* Total row */}
            <tr style={{ background: 'var(--bg-subtle)' }}>
              <td style={{ ...td('left'), fontWeight: 700, color: 'var(--text-secondary)', borderTop: '1px solid var(--border-strong)', borderBottom: 'none' }}>
                Total
              </td>
              <td style={{ ...td('left'), borderTop: '1px solid var(--border-strong)', borderBottom: 'none' }} />
              <td style={{ ...td('right'), fontWeight: 700, borderTop: '1px solid var(--border-strong)', borderBottom: 'none' }}>{fmtNum(totals.td)}</td>
              <td style={{ ...td('right'), fontWeight: 700, borderTop: '1px solid var(--border-strong)', borderBottom: 'none' }}>{fmtNum(totals.rd)}<NotEstimatedFlag n={notEst('rd')} /></td>
              <td style={{ ...td('right'), fontWeight: 700, borderTop: '1px solid var(--border-strong)', borderBottom: 'none' }}>{fmtNum(totals.hid)}</td>
              <td style={{ ...td('right'), fontWeight: 700, borderTop: '1px solid var(--border-strong)', borderBottom: 'none' }}>{fmtNum(totals.sd)}</td>
              <td style={{ ...td('right'), fontWeight: 700, borderTop: '1px solid var(--border-strong)', borderBottom: 'none' }}>{fmtNum(totals.sa)}<NotEstimatedFlag n={notEst('sa')} /></td>
              <td style={{ ...td('right'), fontWeight: 700, borderTop: '1px solid var(--border-strong)', borderBottom: 'none' }}>{fmtNum(totals.hmld)}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Chart tooltip ─────────────────────────────────────────────────────────────
function ChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  const total = payload.reduce((s, p) => s + (p.value || 0), 0);
  return (
    <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 8, padding: '10px 14px', fontSize: 12, minWidth: 150 }}>
      <div style={{ fontWeight: 700, color: 'var(--text-primary)', marginBottom: 6 }}>{label}</div>
      {payload.map(p => (
        <div key={p.name} style={{ color: 'var(--text-secondary)', marginBottom: 2 }}>
          {p.name}: <strong style={{ color: 'var(--text-primary)' }}>{Math.round(p.value).toLocaleString()} m</strong>
        </div>
      ))}
      <div style={{ borderTop: '1px solid var(--border)', marginTop: 6, paddingTop: 6, color: 'var(--text-secondary)' }}>
        Total: <strong style={{ color: 'var(--text-primary)' }}>{Math.round(total).toLocaleString()} m</strong>
      </div>
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────
export default function PlayerChronic({ playerOverride = null }) {
  const { selectedSeasons } = useSeason();
  const [players, setPlayers]           = useState([]);
  const [sessions, setSessions]         = useState([]);
  const [playerId, setPlayerId]         = useUrlParam('player_id', '');
  const [selectedKeys, setSelectedKeys] = useState(new Set());
  const [loading, setLoading]           = useState(true);
  const [exporting, setExporting]       = useState(false);
  // Custom calendar-day range (2026-08-12), alongside the existing week-chip picker — 'weeks'
  // keeps today's behaviour untouched, 'custom' scopes the tables/chart to an exact from/to
  // span instead of whole ISO weeks.
  const [rangeMode, setRangeMode]       = useUrlParam('range_mode', 'weeks'); // 'weeks' | 'custom'
  const [customFrom, setCustomFrom]     = useUrlParam('date_from', '');
  const [customTo, setCustomTo]         = useUrlParam('date_to', '');
  const [availabilityByDate, setAvailabilityByDate] = useState({});
  const contentRef = useRef(null);

  useEffect(() => {
    Promise.all([getGPS(), getPlayers()])
      .then(([gps, pl]) => {
        setSessions(gps || []);
        const list = pl || [];
        setPlayers(list);
        const active = list.filter(p => p.active !== false);
        // Bug fix (2026-09-03): this used to default to the first active player UNCONDITIONALLY,
        // discarding a `player_id` already in the URL (a deep link from Einstein's route_to_page/
        // compose, or any other constructed link) — every such link landed on whichever player
        // happened to sort first, not the one actually requested.
        // Read window.location.search directly rather than the `playerId` (useUrlParam/
        // useSearchParams) binding captured in this effect's closure: this effect has `[]` deps,
        // so that binding is frozen to whatever react-router's search-params state had committed
        // by the FIRST render — and a first fix using `!playerId` here still showed the wrong
        // player live, meaning that commit can lag one tick behind the actual browser URL that
        // the SAME click's handlePageChange (App.js) already wrote via history.replaceState.
        // window.location.search is the browser's own live address bar, not react-managed state,
        // so it can't be stale relative to a navigation that already happened.
        const urlPlayerId = new URLSearchParams(window.location.search).get('player_id');
        if (playerOverride) setPlayerId(String(playerOverride.id)); else if (!urlPlayerId && active.length) setPlayerId(String(active[0].id));
      })
      .finally(() => setLoading(false));
  }, []);

  // All sessions for selected player, sorted ascending by date
  const playerSessions = useMemo(
    () => (playerId ? computePlayerSessions(playerId, sessions, selectedSeasons) : []),
    [sessions, playerId, selectedSeasons]
  );

  // All available weeks for this player, most recent first
  const allWeeks = useMemo(() => groupIntoWeeks(playerSessions), [playerSessions]);

  // Auto-select the 4 most recent weeks whenever the player or data changes
  useEffect(() => {
    setSelectedKeys(new Set(allWeeks.slice(0, 4).map(w => w.key)));
  }, [allWeeks]);

  const toggleWeek = key => {
    setSelectedKeys(prev => {
      const next = new Set(prev);
      if (next.has(key)) {
        if (next.size > 1) next.delete(key); // always keep at least one
      } else {
        next.add(key);
      }
      return next;
    });
  };

  // Weeks to display, most recent first — still the source for the week-chip toggles
  // themselves (which weeks actually have session data to offer), independent of
  // rangeMode/renderBuckets below.
  const weekGroups = useMemo(() =>
    allWeeks.filter(w => selectedKeys.has(w.key)),
    [allWeeks, selectedKeys]
  );

  const sessionsByDate = useMemo(() => {
    const map = {};
    for (const s of playerSessions) map[s.date] = s;
    return map;
  }, [playerSessions]);

  // The exact calendar dates in scope: for 'weeks' mode, every day (Monday..Sunday) of each
  // selected week, not just the days that happen to have a session — otherwise there's no row
  // to show an unavailable-but-session-less day on. For 'custom' mode, the picked from/to span.
  const activeDates = useMemo(() => {
    if (rangeMode === 'custom') {
      if (!customFrom || !customTo || customFrom > customTo) return [];
      return dateRangeInclusive(customFrom, customTo);
    }
    const set = new Set();
    weekGroups.forEach(w => {
      const [mon, sun] = isoWeekBounds(w.year, w.weekNum);
      dateRangeInclusive(mon, sun).forEach(d => set.add(d));
    });
    return [...set].sort();
  }, [rangeMode, customFrom, customTo, weekGroups]);

  // Per-day availability for the selected player across activeDates — reuses the existing
  // /api/availability endpoint (Medical's day-by-day grid, Availability.js's Season Analysis
  // tab) rather than re-deriving injury/illness/override logic in this file; that endpoint
  // already returns the whole squad, so this just picks out the one player's `days` array.
  useEffect(() => {
    if (!playerId || activeDates.length === 0) { setAvailabilityByDate({}); return; }
    let cancelled = false;
    getAvailability(activeDates[0], activeDates[activeDates.length - 1])
      .then(res => {
        if (cancelled) return;
        const playerRow = (res.players || []).find(p => String(p.player_id) === playerId);
        const map = {};
        (playerRow?.days || []).forEach(d => { map[d.date] = d; });
        setAvailabilityByDate(map);
      })
      .catch(() => { if (!cancelled) setAvailabilityByDate({}); });
    return () => { cancelled = true; };
  }, [playerId, activeDates]);

  // Final table content: one bucket per ISO week touched by activeDates, each holding every day
  // that either has a session or is flagged unavailable — an ordinary rest day with neither
  // stays invisible, same as this page's behaviour before this feature existed.
  const renderBuckets = useMemo(() => {
    const map = new Map();
    activeDates.forEach(date => {
      const session = sessionsByDate[date] || null;
      const avail = availabilityByDate[date] || null;
      const unavailable = avail && avail.status === 'unavailable';
      if (!session && !unavailable) return;
      const key = isoKey(date);
      if (!map.has(key)) map.set(key, { key, weekNum: getISOWeek(date), year: getISOYear(date), days: [] });
      map.get(key).days.push({ date, session, avail: unavailable ? avail : null });
    });
    return [...map.values()]
      .sort((a, b) => b.key.localeCompare(a.key))
      .map(bucket => ({ ...bucket, days: bucket.days.sort((a, b) => b.date.localeCompare(a.date)) }));
  }, [activeDates, sessionsByDate, availabilityByDate]);

  // Chart data in chronological order (oldest → newest, left → right)
  const chartData = useMemo(() =>
    [...renderBuckets]
      .reverse()
      .map(b => {
        const rows = b.days.filter(d => d.session).map(d => d.session);
        return { label: `W${b.weekNum}`, hid: sumField(rows, 'hid'), expl: sumField(rows, 'expl') };
      }),
    [renderBuckets]
  );

  const hasData = allWeeks.length > 0;
  const hasAnyPlayerData = useMemo(() => {
    if (!playerId) return false;
    return sessions.some(s => (s.players || []).some(p => String(p.player_id) === playerId));
  }, [sessions, playerId]);

  // Same captureBlocks/packBlocksIntoPages/A4-assembly pipeline as Week Summary and the
  // Physical Report (src/pdfExport.js) — this used to be its own bespoke single-page,
  // content-height-sized PDF (new jsPDF({format:[pdfW, dynamicPdfH]})). That broke whenever the
  // computed height came out shorter than the fixed 595.28pt width (e.g. one or two short weeks
  // selected): jsPDF's default portrait orientation silently swaps a format array's two numbers
  // when width > height, so the page rendered narrower than the image drawn onto it — the
  // right-hand columns and part of the chart got clipped off, with the leftover width padded
  // out as blank space at the bottom. Standard multi-page A4 export, marking each week's table
  // and the chart as its own data-report-block, sidesteps that entirely (and matches every
  // other export in the app instead of a second implementation).
  const handleExport = async () => {
    if (!contentRef.current) return;
    setExporting(true);
    try {
      const bgColor = getComputedStyle(document.documentElement)
        .getPropertyValue('--bg-app').trim() || '#0f172a';
      const arrayBuffer = await renderReportPdf(contentRef.current, bgColor, { scale: 1.5, imageFormat: 'JPEG', imageQuality: 0.85 });
      const blob = new Blob([arrayBuffer], { type: 'application/pdf' });
      const playerName = players.find(p => String(p.id) === playerId)?.name || 'player';
      downloadBlob(blob, `chronic-report-${playerName.replace(/\s+/g, '-').toLowerCase()}.pdf`);
    } catch (err) {
      console.error('PDF export failed:', err);
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh', color: 'var(--text-muted)', fontSize: '0.9rem' }}>
        Loading…
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto', paddingBottom: 40 }}>

      {/* ── Header ── */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 24, gap: 16 }}>
        <div>
          <h2 style={{ fontSize: '1.2rem', fontWeight: 700, color: 'var(--text-primary)', margin: 0 }}>
            Player Chronic Report
          </h2>
          <p style={{ fontSize: '0.83rem', color: 'var(--text-muted)', margin: '4px 0 0' }}>
            Weekly GPS load summary
          </p>
        </div>
        {hasData && (
          <button
            onClick={handleExport}
            disabled={exporting}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 7,
              padding: '8px 16px',
              borderRadius: 9,
              border: '1px solid var(--border-strong)',
              background: exporting ? 'var(--border)' : 'var(--bg-card)',
              color: exporting ? 'var(--text-dimmed)' : 'var(--text-secondary)',
              fontSize: '0.84rem',
              fontWeight: 600,
              cursor: exporting ? 'not-allowed' : 'pointer',
              fontFamily: 'inherit',
              transition: 'all 0.15s',
              flexShrink: 0,
            }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
              <polyline points="7 10 12 15 17 10" />
              <line x1="12" y1="15" x2="12" y2="3" />
            </svg>
            {exporting ? 'Exporting…' : 'PDF Export'}
          </button>
        )}
      </div>

      {/* ── Controls ── */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16, marginBottom: 24, flexWrap: 'wrap' }}>
        {!playerOverride && (
          <select
            value={playerId}
            onChange={e => setPlayerId(e.target.value)}
            style={selectStyle}
          >
            {players.filter(p => p.active !== false).map(p => (
              <option key={p.id} value={String(p.id)}>
                {p.name || p.username || `Player ${p.id}`}
              </option>
            ))}
          </select>
        )}

        {/* Weeks / Custom range mode toggle */}
        {allWeeks.length > 0 && (
          <div style={{ display: 'flex', borderRadius: 8, border: '1px solid var(--border)', overflow: 'hidden', flexShrink: 0 }}>
            {[['weeks', 'Weeks'], ['custom', 'Custom range']].map(([mode, label]) => (
              <button
                key={mode}
                onClick={() => setRangeMode(mode)}
                style={{
                  padding: '7px 13px',
                  border: 'none',
                  background: rangeMode === mode ? 'rgba(59,130,246,0.15)' : 'transparent',
                  color: rangeMode === mode ? '#93c5fd' : 'var(--text-muted)',
                  fontSize: '0.82rem',
                  fontWeight: rangeMode === mode ? 600 : 400,
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                  transition: 'all 0.15s',
                }}
              >
                {label}
              </button>
            ))}
          </div>
        )}

        {/* Week filter chips */}
        {rangeMode === 'weeks' && allWeeks.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
            {allWeeks.map(w => {
              const on = selectedKeys.has(w.key);
              return (
                <button
                  key={w.key}
                  onClick={() => toggleWeek(w.key)}
                  style={{
                    padding: '7px 13px',
                    borderRadius: 8,
                    border: on ? '1px solid #3b82f6' : '1px solid var(--border)',
                    background: on ? 'rgba(59,130,246,0.15)' : 'transparent',
                    color: on ? '#93c5fd' : 'var(--text-muted)',
                    fontSize: '0.82rem',
                    fontWeight: on ? 600 : 400,
                    cursor: 'pointer',
                    fontFamily: 'inherit',
                    transition: 'all 0.15s',
                  }}
                >
                  W{w.weekNum}
                </button>
              );
            })}
          </div>
        )}

        {/* Custom calendar-day range */}
        {rangeMode === 'custom' && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <input
              type="date"
              value={customFrom}
              onChange={e => setCustomFrom(e.target.value)}
              style={selectStyle}
            />
            <span style={{ color: 'var(--text-muted)', fontSize: '0.82rem' }}>until</span>
            <input
              type="date"
              value={customTo}
              onChange={e => setCustomTo(e.target.value)}
              style={selectStyle}
            />
          </div>
        )}
      </div>

      {/* ── Empty state: no scoped range selected/no sessions or absences within it ── */}
      {hasData && renderBuckets.length === 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 200, color: 'var(--text-muted)', textAlign: 'center', gap: 8 }}>
          <p style={{ margin: 0, fontSize: '0.9rem', color: 'var(--text-secondary)' }}>
            {rangeMode === 'custom' && (!customFrom || !customTo)
              ? 'Pick a from and until date to view this range.'
              : 'No sessions or availability issues in this range.'}
          </p>
        </div>
      )}

      {/* ── Empty state ── */}
      {!hasData && (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 320, color: 'var(--text-muted)', textAlign: 'center', gap: 12 }}>
          <svg width="44" height="44" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.2} opacity={0.4}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>
          <div>
            {hasAnyPlayerData ? (
              <>
                <p style={{ margin: 0, fontSize: '0.9rem', color: 'var(--text-secondary)' }}>No data for this season.</p>
                <p style={{ margin: '4px 0 0', fontSize: '0.82rem' }}>Change the season filter in Settings to view other data.</p>
              </>
            ) : (
              <>
                <p style={{ margin: 0, fontSize: '0.9rem', color: 'var(--text-secondary)' }}>No GPS data found for this player.</p>
                <p style={{ margin: '4px 0 0', fontSize: '0.82rem' }}>Upload sessions in the Upload zone first.</p>
              </>
            )}
          </div>
        </div>
      )}

      {/* ── Content ── */}
      {hasData && renderBuckets.length > 0 && (
        <div ref={contentRef}>

          {/* Report header — player photo left, Heracles crest right */}
          <div data-report-block="true" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20, paddingBottom: 18, borderBottom: '1px solid var(--border)' }}>
            <PlayerAvatar player={players.find(p => String(p.id) === playerId)} />
            <img
              src={`${process.env.PUBLIC_URL}/heracles-crest.png`}
              alt="Heracles Almelo"
              style={{ height: 56, width: 'auto', objectFit: 'contain' }}
            />
          </div>

          {/* Weekly tables — each its own block so a page break never lands mid-table */}
          {renderBuckets.map(b => (
            <div key={b.key} data-report-block="true">
              <WeekTable weekNum={b.weekNum} days={b.days} />
            </div>
          ))}

          {/* Stacked bar chart — full width below tables */}
          <div data-report-block="true" style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 14, padding: '20px 24px 16px', marginTop: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 14 }}>
              <span style={{ fontSize: '0.86rem', fontWeight: 700, color: 'var(--text-primary)' }}>
                HID + Explosive Dist.
              </span>
              {/* Legend */}
              <div style={{ display: 'flex', gap: 14 }}>
                {[
                  { color: '#60a5fa', label: 'HID' },
                  { color: '#1d4ed8', label: 'Expl. Dist.' },
                ].map(({ color, label }) => (
                  <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                    <div style={{ width: 9, height: 9, borderRadius: 2, background: color }} />
                    <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>{label}</span>
                  </div>
                ))}
              </div>
            </div>
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={chartData} margin={{ top: 8, right: 24, left: 0, bottom: 0 }} barCategoryGap="20%">
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
                <XAxis
                  dataKey="label"
                  tick={{ fontSize: 12, fill: 'var(--text-muted)' }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  tick={{ fontSize: 11, fill: 'var(--text-muted)' }}
                  axisLine={false}
                  tickLine={false}
                  width={48}
                  tickFormatter={v => v >= 1000 ? `${Math.round(v / 1000)}k` : String(v)}
                  label={{ value: 'meters', angle: -90, position: 'insideLeft', offset: 12, style: { fontSize: 10, fill: 'var(--text-dimmed)' } }}
                />
                <Tooltip content={<ChartTooltip />} />
                <Bar dataKey="hid" name="HID" fill="#60a5fa" stackId="stack" radius={[0, 0, 4, 4]}>
                  <LabelList
                    dataKey="hid"
                    position="inside"
                    fill="#0f172a"
                    fontSize={11}
                    fontWeight={700}
                    formatter={v => v >= 150 ? Math.round(v).toLocaleString() : ''}
                  />
                </Bar>
                <Bar dataKey="expl" name="Expl. Dist." fill="#1d4ed8" stackId="stack" radius={[4, 4, 0, 0]}>
                  <LabelList
                    dataKey="expl"
                    position="inside"
                    fill="#fff"
                    fontSize={11}
                    fontWeight={700}
                    formatter={v => v >= 150 ? Math.round(v).toLocaleString() : ''}
                  />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>

        </div>
      )}
    </div>
  );
}

// ── Shared styles ──────────────────────────────────────────────────────────────
const selectStyle = {
  padding: '8px 32px 8px 12px',
  borderRadius: 8,
  border: '1px solid var(--border-strong)',
  background: 'var(--bg-card)',
  color: 'var(--text-primary)',
  fontSize: '0.88rem',
  fontFamily: 'inherit',
  cursor: 'pointer',
  outline: 'none',
  minWidth: 190,
  appearance: 'none',
  WebkitAppearance: 'none',
};
